<template>
  <Sidebar
    :visible="visible && !isHiddenForSelection"
    position="left"
    class="flow-operator-panel w-30rem"
    :show-close-icon="false"
    :dismissable="mode === 'list'"
    @update:visible="handleClose"
  >
    <!-- List mode -->
    <template v-if="mode === 'list'">
      <div class="flow-operator-panel__header">
        <h2>{{ t("flows.operatorPanel.title") }}</h2>
        <Button
          icon="far fa-times"
          class="p-button-text p-button-rounded"
          @click="handleClose"
        />
      </div>

      <div class="flow-operator-panel__search">
        <span class="p-input-icon-left w-full">
          <i class="far fa-search" />
          <InputText
            v-model="searchQuery"
            :placeholder="t('flows.operatorPanel.search')"
            class="w-full"
          />
        </span>
      </div>

      <TabMenu
        v-model:activeIndex="activeTab"
        :model="tabItems"
        class="flow-operator-panel__tabs"
      />

      <div class="flow-operator-panel__content">
        <div
          v-if="currentOperators.length > 0"
          class="flow-operator-panel__list"
        >
          <div
            v-for="op in currentOperators"
            :key="op.id"
            class="flow-operator-panel__item"
            :class="[
              `flow-operator-panel__item--${op.kind}`,
              { 'flow-operator-panel__item--disabled': getOperatorFlag(op.id)?.enabled === false }
            ]"
            @click="getOperatorFlag(op.id)?.enabled === false ? null : selectOperator(op)"
          >
            <div class="flow-operator-panel__item-icon">
              <i :class="`far ${op.icon}`" />
            </div>
            <div class="flow-operator-panel__item-label">
              {{ t(op.label) }}
              <ComingSoonBadge
                v-if="getOperatorFlag(op.id)?.comingSoon"
                variant="inline"
              />
            </div>
          </div>
        </div>
        <div v-else class="flow-operator-panel__empty">
          <div class="empty-state-icon mb-3">
            <i
              :class="
                searchQuery
                  ? 'far fa-search-minus fa-2x text-gray-400'
                  : 'far fa-cube fa-2x text-gray-400'
              "
            ></i>
          </div>
          {{
            searchQuery
              ? t("flows.operatorPanel.noSearchResults")
              : t("flows.operatorPanel.noResults")
          }}
        </div>
      </div>
    </template>

    <!-- Form mode -->
    <FlowOperatorForm
      v-else-if="mode === 'form' && selectedOperator"
      :operator="selectedOperator"
      :is-edit-mode="isEditMode"
      :is-replace-mode="isReplaceMode"
      :operator-id="editStep?.id || replaceStep?.id"
      v-model="formData"
      @submit="confirmAdd"
      @cancel="backToList"
      @close="handleClose"
      @update-step="$emit('update-step', $event)"
    />
  </Sidebar>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, shallowRef, watch, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import Sidebar from 'primevue/sidebar';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import TabMenu from 'primevue/tabmenu';
import FlowOperatorForm from './FlowOperatorForm.vue';
import type { FlowOperatorMetadata } from '@/types/flow-types';
import type { FlowStructureDto } from '@/types/flow-structure';
import {
  getCompatibleOperators,
  getIncompatibleEventValues,
  getOperatorById,
  groupOperatorsByKind,
  OperatorInsertContext,
} from './operators/registry';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { useServiceVerification } from '@/composables/flows/useServiceVerification';
import { getFeatureFlagForOperator } from '@/configs/feature-flags';
import ComingSoonBadge from '@/components/shared/ComingSoonBadge.vue';

type PanelMode = 'list' | 'form';

export default defineComponent({
  name: 'FlowOperatorPanel',

  components: {
    Sidebar,
    Button,
    InputText,
    TabMenu,
    FlowOperatorForm,
    ComingSoonBadge,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    context: {
      type: Object as PropType<OperatorInsertContext | null>,
      default: null,
    },
    editStep: {
      type: Object as PropType<{ id: string; type: string; parameters: Record<string, any> } | null>,
      default: null,
    },
    replaceStep: {
      type: Object as PropType<{ id: string; type: string } | null>,
      default: null,
    },
    flow: {
      type: Object as PropType<FlowStructureDto | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'add', 'save', 'replace', 'decrease-branches', 'update-step'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Provide flow and context to child components (for LinkedOperatorSelector)
    const flowRef = computed(() => props.flow);
    const contextRef = computed(() => props.context);
    provide('flow', flowRef);
    provide('insertContext', contextRef);
    // Events incompatible as a child of the insertion parent — the internal-event
    // form hides them from its event picker (clean UX vs. post-hoc warning).
    const forbiddenEventValues = computed(() => getIncompatibleEventValues(props.context));
    provide('forbiddenEventValues', forbiddenEventValues);

    // State for hiding panel during selection mode (without losing form state)
    const isHiddenForSelection = ref(false);

    // Provide reopen panel callback for LinkedOperatorSelector
    const reopenPanel = () => {
      isHiddenForSelection.value = false;
    };
    provide('reopenPanel', reopenPanel);

    // Handle start-selection event from LinkedOperatorSelector
    const handleStartSelection = () => {
      // Hide the panel visually when entering selection mode (but keep state)
      isHiddenForSelection.value = true;
    };
    provide('handleStartSelection', handleStartSelection);

    // Provide updateStep callback so composables can persist data without user clicking save
    const updateStep = (payload: { stepId: string; data: Record<string, any> }) => {
      emit('update-step', payload);
    };
    provide('updateStep', updateStep);
    const searchQuery = ref('');
    const activeTab = ref(0);
    const mode = ref<PanelMode>('list');
    const selectedOperator = shallowRef<FlowOperatorMetadata | null>(null);
    const formData = ref<any>(null);

    const compatibleOperators = computed(() => {
      if (!props.context) return [];
      return getCompatibleOperators(props.context);
    });

    const groupedOperators = computed(() => {
      return groupOperatorsByKind(compatibleOperators.value);
    });

    const filterBySearch = (operators: FlowOperatorMetadata[]) => {
      if (!searchQuery.value.trim()) return operators;
      const query = searchQuery.value.toLowerCase();
      return operators.filter(op => {
        const label = t(op.label).toLowerCase();
        return label.includes(query);
      });
    };

    const filteredTriggers = computed(() => filterBySearch(groupedOperators.value.triggers));
    const filteredActions = computed(() => filterBySearch(groupedOperators.value.actions));
    const filteredFilters = computed(() => filterBySearch(groupedOperators.value.filters));

    const filteredOperators = computed(() => [
      ...filteredTriggers.value,
      ...filteredActions.value,
      ...filteredFilters.value,
    ]);

    const tabItems = computed(() => {
      const items: { label: string; key: string }[] = [];
      if (groupedOperators.value.triggers.length > 0) {
        items.push({ label: t('flows.operatorKinds.trigger'), key: 'triggers' });
      }
      if (groupedOperators.value.actions.length > 0) {
        items.push({ label: t('flows.operatorKinds.action'), key: 'actions' });
      }
      if (groupedOperators.value.filters.length > 0) {
        items.push({ label: t('flows.operatorKinds.filter'), key: 'filters' });
      }
      return items;
    });

    const currentOperators = computed(() => {
      const key = tabItems.value[activeTab.value]?.key;
      if (key === 'triggers') return filteredTriggers.value;
      if (key === 'actions') return filteredActions.value;
      if (key === 'filters') return filteredFilters.value;
      return [];
    });

    // Service verification
    const { verifyAndShowModalIfNeeded } = useServiceVerification();

    const openOperatorForm = (operator: FlowOperatorMetadata) => {
      selectedOperator.value = operator;
      formData.value = operator.defaultData(undefined, props.context?.campaignType);
      mode.value = 'form';
    };

    const selectOperator = async (operator: FlowOperatorMetadata) => {
      // Verify services before opening the form
      const canProceed = await verifyAndShowModalIfNeeded(
        operator.id,
        () => openOperatorForm(operator),
      );

      if (canProceed) {
        openOperatorForm(operator);
      }
    };

    const backToList = () => {
      // In edit mode, close the panel instead of going back to list
      if (isEditMode.value) {
        emit('update:visible', false);
        return;
      }
      mode.value = 'list';
      selectedOperator.value = null;
      formData.value = null;
    };

    const handleClose = () => {
      emit('update:visible', false);
    };

    const isEditMode = computed(() => !!props.editStep);
    const isReplaceMode = computed(() => !!props.replaceStep);

    const confirmAdd = (data?: any) => {
      if (selectedOperator.value) {
        let finalData = data ?? formData.value;

        // Handle branch decrease in edit mode before cleaning data
        if (isEditMode.value && props.editStep) {
          const initialOutputs = finalData.initial_number_outputs;
          const outputsToDelete = finalData.outputs_to_delete;
          const newOutputs = selectedOperator.value.outputs?.(finalData) ?? 1;

          console.log('[FlowOperatorPanel] Branch decrease check:', {
            initialOutputs,
            newOutputs,
            outputsToDelete,
            finalData,
          });

          // For ABTest: use outputs_to_delete array
          if (outputsToDelete && Array.isArray(outputsToDelete) && outputsToDelete.length > 0) {
            console.log('[FlowOperatorPanel] Emitting decrease-branches for ABTest:', outputsToDelete);
            emit('decrease-branches', {
              stepId: props.editStep.id,
              outputsToRemove: outputsToDelete,
            });
          }
          // For filters: if outputs decreased from 2 to 1, remove output_2
          else if (initialOutputs !== undefined && initialOutputs !== null && newOutputs < initialOutputs) {
            console.log('[FlowOperatorPanel] Emitting decrease-branches for filter');
            emit('decrease-branches', {
              stepId: props.editStep.id,
              outputsToRemove: ['output_2'],
            });
          }
        }

        // Call beforeInsert if defined to transform data before saving
        if (selectedOperator.value.beforeInsert) {
          finalData = selectedOperator.value.beforeInsert(finalData);
        }

        if (isEditMode.value && props.editStep) {
          emit('save', {
            stepId: props.editStep.id,
            data: finalData,
          });
        } else if (isReplaceMode.value && props.replaceStep) {
          emit('replace', {
            operator: selectedOperator.value,
            data: finalData,
          });
        } else {
          emit('add', {
            operator: selectedOperator.value,
            data: finalData,
          });
        }
        emit('update:visible', false);
      }
    };


    watch([() => props.visible, () => props.editStep, () => props.replaceStep, () => props.context], ([newVisible, newEditStep, newReplaceStep, newContext]) => {
      if (newVisible) {
        if (newEditStep) {
          // Edit mode: open form directly with existing data
          const operator = getOperatorById(newEditStep.type);
          if (operator) {
            selectedOperator.value = operator;
            // Parse parameters back to form data format
            const parsedParams = parseOperatorParameters(newEditStep.parameters);
            const baseData = { ...operator.defaultData(undefined, props.context?.campaignType), ...parsedParams };

            // Store initial number of outputs for tracking changes
            if (operator.outputs) {
              baseData.initial_number_outputs = operator.outputs(baseData);
            }

            formData.value = baseData;
            mode.value = 'form';
          }
        } else if (newReplaceStep) {
          // Replace mode: show operator list to select replacement
          searchQuery.value = '';
          activeTab.value = 0;
          mode.value = 'list';
          selectedOperator.value = null;
          formData.value = null;
        } else if (newContext?.preSelectedOperator) {
          // Pre-selected operator: go directly to form (for bulk campaigns empty state)
          selectedOperator.value = newContext.preSelectedOperator;
          formData.value = newContext.preSelectedOperator.defaultData(undefined, newContext.campaignType);
          mode.value = 'form';
        } else {
          // Add mode: show operator list
          searchQuery.value = '';
          activeTab.value = 0;
          mode.value = 'list';
          selectedOperator.value = null;
          formData.value = null;
        }
      }
    }, { immediate: true });

    const getOperatorFlag = getFeatureFlagForOperator;

    return {
      t,
      searchQuery,
      getOperatorFlag,
      activeTab,
      mode,
      selectedOperator,
      formData,
      groupedOperators,
      filteredTriggers,
      filteredActions,
      filteredFilters,
      filteredOperators,
      tabItems,
      currentOperators,
      selectOperator,
      backToList,
      handleClose,
      confirmAdd,
      isEditMode,
      isReplaceMode,
      isHiddenForSelection,
    };
  },
});
</script>

<style lang="scss">
.flow-operator-panel.p-sidebar {
  border: none;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.08);

  .p-sidebar-header {
    display: none !important;
  }

  .p-sidebar-content {
    padding: 0 !important;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.flow-operator-panel {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;

    h2 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #1f2937;
    }

    .p-button {
      width: 28px !important;
      height: 28px !important;
      min-width: 28px !important;
      padding: 0 !important;
      color: #9ca3af !important;
      background: transparent !important;
      border: none !important;
      border-radius: 50% !important;

      &:hover {
        background: #f3f4f6 !important;
        color: #6b7280 !important;
      }

      &:focus {
        box-shadow: none !important;
      }
    }
  }

  &__search {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #f3f4f6;

    .p-input-icon-left {
      width: 100%;

      > i {
        left: 0.875rem;
        color: #9ca3af;
      }
    }

    .p-inputtext {
      width: 100%;
      padding: 0.625rem 0.875rem 0.625rem 2.25rem;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      font-size: 0.8125rem;
      background: #f9fafb;

      &::placeholder {
        color: #9ca3af;
      }

      &:focus {
        background: #fff;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
  }

  &__tabs {
    padding: 0 1rem;

    .p-tabmenu-nav {
      border: none;
      background: transparent;
    }

    .p-menuitem-link {
      background: transparent !important;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.025em;
      padding: 0.75rem 0.5rem;
      margin: 0 0 -2px 0;
      color: #6b7280;
      border: solid transparent;
      border-width: 0 0 2px 0;

      &:hover {
        color: #374151;
        border-color: #9ca3af;
      }

      &:focus {
        box-shadow: none !important;
      }
    }

    .p-tabmenuitem.p-highlight .p-menuitem-link {
      color: #374151 !important;
      border-color: #90d51c !important;
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
  }

  &__list {
    padding: 0.75rem;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0.75rem;
    border-radius: 0.625rem;
    cursor: pointer;
    transition: background-color 0.15s, border-color 0.15s;
    margin-bottom: 0.25rem;
    border: 1px solid transparent;

    &:hover {
      background-color: #f9fafb;
      border-color: #e5e7eb;
    }

    &:active {
      transform: scale(0.98);
    }

    &-icon {
      width: 36px;
      height: 36px;
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 0.875rem;
      flex-shrink: 0;
    }

    &-label {
      font-size: 0.8125rem;
      color: #374151;
      font-weight: 500;
    }

    &--disabled {
      opacity: 0.45;
      pointer-events: none;
      cursor: default;
    }

    &--trigger &-icon {
      background: linear-gradient(135deg, #ffa31a, #ffb347);
      box-shadow: 0 2px 8px rgba(255, 163, 26, 0.3);
    }

    &--action &-icon {
      background: linear-gradient(135deg, #7dd321, #9be15d);
      box-shadow: 0 2px 8px rgba(125, 211, 33, 0.3);
    }

    &--filter &-icon {
      background: linear-gradient(135deg, #67b8f2, #8fc8ff);
      box-shadow: 0 2px 8px rgba(103, 184, 242, 0.3);
    }
  }

  &__empty {
    padding: 3rem 1.5rem;
    text-align: center;
    color: #9ca3af;
    font-size: 0.8125rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%; // Fill the available space

    .empty-state-icon {
      margin-bottom: 0.5rem;
      color: #d1d5db; // Light gray for icon
    }
  }
}
</style>
